import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/Button';
import { useFormInput } from 'src/components/FormInput';
import { PROMOTION_VALIDATE_ERROR } from 'src/constants/validate';
import { useModal } from 'src/ModalContext';
import { getProductPromotion } from 'src/services/checkout';

type Props = {
  promotion: CheckoutPromotionCodeData;
  setPromotion: (value: CheckoutPromotionCodeData) => void;
  product: CheckoutProductData;
};

type ReturnType = {
  promotion: CheckoutPromotionCodeData;
  PromotionCode: JSX.Element;
};

export default function PromotionCode({ promotion, setPromotion, product }: Props): JSX.Element {
  const { showModal } = useModal();
  const { t } = useTranslation();

  const promotionInput = useFormInput({
    inputClassName: 'N_14_R text-brown-grey',
    mode: 'trigger',
    errorStyle: {
      marginRight: '-51%',
    },
    validate: {
      required: (value) => !!value,
      verify: async (value) => {
        try {
          const promotionData = await getProductPromotion(product.id, value);
          setPromotion(promotionData.promotion);
          return true;
        } catch (e) {
          console.error(e);
          return false;
        }
      },
    },
    errors: PROMOTION_VALIDATE_ERROR,
  });

  const onVerifyPromotion = useCallback(() => {
    promotionInput.triggerValidate();
    if (promotionInput.value === '') {
      showModal({
        message: t('payment/promotion_validate_required_error'),
        success: () => {
          console.log('success');
        },
      });
      return;
    }
  }, [product.id, promotionInput]);

  const onCancelPromotion = useCallback(() => {
    promotionInput.setValue('');
    promotionInput.resetValidate();
    setPromotion({} as CheckoutPromotionCodeData);
  }, [promotionInput]);

  return (
    <>
      <div className="mt-8">{t('payment/promotion_code_title')}</div>
      <div className="mt-4 flex justify-between items-start">
        <div className="w-2/3">{promotionInput.CustomFormInput}</div>
        <div className="ml-2 w-1/3">
          {promotion && promotion.id ? (
            <Button title={t('payment/promotion_button_cancel')} onClick={onCancelPromotion} />
          ) : (
            <Button title={t('payment/promotion_button_register')} onClick={onVerifyPromotion} />
          )}
        </div>
      </div>
    </>
  );
}

export const usePromotionCode = (options: { product: CheckoutProductData }): ReturnType => {
  const [promotion, setPromotion] = useState({} as CheckoutPromotionCodeData);
  return {
    promotion,
    PromotionCode: (
      <PromotionCode
        {...{
          promotion,
          setPromotion,
          ...options,
        }}
      />
    ),
  };
};
